import React, { useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import {createHomeParagraph, getHomeParagraph, updateHomeParagraph} from "../../../api/axios";
import CmsCircularProgress from "../../Common/CircularProgress";
import removeIcon from "../../../Assets/remove-image-photo.svg";
import { showToast } from "../../../Utills/toast";
import FormGroup from "../../Common/FormGroup";
import { Formik } from "formik";
import * as Yup from "yup";

// Enhanced Validation Schema
const validationSchema = Yup.object({
  paragraph_image: Yup.mixed()
    .test('required', 'Paragraph image is required', function(value) {
      const { old_paragraph_image } = this.parent;
      if (!value && !old_paragraph_image) {
        return false;
      }
      return true;
    }),
  paragraph: Yup.string()
    .trim()
    .required('Paragraph is required'),
});

export default function ParagraphEdit() {
  const { id, partnerId } = useParams();
  const [homeParagraph, setHomeParagraph] = useState({
    paragraph_image: "",
    paragraph: "",
    index: null,
  });
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Initial values for Formik
  const initialValues = {
    paragraph_image: homeParagraph.paragraph_image || '',
    paragraph: homeParagraph.paragraph || "",
    old_paragraph_image: homeParagraph.paragraph_image || '',
  };

  const fetchParagraphData = async () => {
    setIsLoading(true);
    if (!Number(id)) {
      setIsLoading(false);
      return;
    }
    
    try {
      const response = await getHomeParagraph(id);
      const paragraphData = response.data.data[0];
      setHomeParagraph({
        ...paragraphData,
        index: response.data.data.indexOf(paragraphData),
      });
    } catch (error) {
     showToast({
      type: 'error',
      message: error.response?.data?.message || 'Failed to fetch paragraph data. Please try again.',
     });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchParagraphData();
  }, [id]);

  const handleSubmit = async (values, {setFieldError, setFieldValue }) => {
    try {
      setSaving(true);

      var formData = new FormData();
      formData.append("paragraph", values.paragraph.trim());

      if (values.paragraph_image instanceof File) {
        formData.append("paragraph_image", values.paragraph_image);
      }

      if (!Number(id)) {
        // Create mode
        try {
          formData.append("partner_id", partnerId);
          const res = await createHomeParagraph(formData);
          
          if (res.status === 200) {
            showToast({
              type: 'success',
              message: res?.data?.message || "Paragraph has been created successfully!",
            });
            const paragraphData = res.data.data;
            setHomeParagraph(paragraphData);
            if (paragraphData && paragraphData.id) {
              navigate(`/${partnerId}/home/paragraph/edit/${paragraphData.id}`);
            }
          } else {
            throw new Error(res?.data?.message || "Failed to create paragraph section.");
          }
        } catch (err) {
          const errorMessage = err?.response?.data?.message || err.message || "Failed to create paragraph section. Please try again.";
          showToast({
            type: 'error',
            message: errorMessage,
          });
          
          // Handle specific field errors
          if (err?.response?.data?.errors) {
            Object.keys(err.response.data.errors).forEach(field => {
              setFieldError(field, err.response.data.errors[field][0]);
            });
          }
          console.error(err);
        }
      } else {
        // Update mode
        try {
          const res = await updateHomeParagraph(id, formData);
          
          if (res.status === 200) {
            showToast({
              type: 'success',
              message: res?.data?.message || "Paragraph has been updated successfully!",
            });
            const updatedParagraphData = res?.data?.data; 
            if (updatedParagraphData) {
              if (updatedParagraphData.paragraph_image) {
                setFieldValue("paragraph_image", updatedParagraphData.paragraph_image);
                setFieldValue("old_paragraph_image", updatedParagraphData.paragraph_image);
              }
              setHomeParagraph(prev => ({
                ...prev,
                paragraph_image: updatedParagraphData.paragraph_image,
                paragraph: updatedParagraphData.paragraph,
              }));
            }
            // // Refresh data after successful update
            // await fetchParagraphData();
          } else {
            showToast({
              type: 'error',
              message: res?.data?.message || "Failed to update paragraph section.",
            });
          }
        } catch (err) {
          const errorMessage = err?.response?.data?.message || err.message || "Failed to update paragraph section. Please try again.";
          showToast({
            type: 'error',
            message: errorMessage,
          });
          
          // Handle specific field errors
          if (err?.response?.data?.errors) {
            Object.keys(err.response.data.errors).forEach(field => {
              setFieldError(field, err.response.data.errors[field][0]);
            });
          }
          console.error(err);
        }
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'An unexpected error occurred. Please try again.';
      showToast({
        type: 'error',
        message: errorMessage,
      });
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {isLoading ? <CmsCircularProgress/> : 
        <FormWrapper>
          <Formik
            initialValues={{
              ...initialValues,
              paragraph: homeParagraph.paragraph || "",
              old_paragraph_image: homeParagraph.paragraph_image,
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ values, errors, touched, setFieldValue, handleSubmit, isSubmitting, getFieldProps }) => {
              return (
                <Form onSubmit={handleSubmit} noValidate>
                  <Title>{Number(id) ? 'Edit' : 'Add'} paragraph section</Title>
                  
                  <FormGroup
                    label="Select an image *"
                    error={errors.paragraph_image && touched.paragraph_image ? errors.paragraph_image : ''}
                  >
                    <Form.Control
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        setFieldValue('paragraph_image', file);
                      }}
                      style={{ display: "none" }}
                      ref={inputRef}
                      isInvalid={errors.paragraph_image && touched.paragraph_image}
                    />
                      { values.paragraph_image ? (
                      <ImageWrapper>
                        <UploadImage>
                          <BootstrapImage
                            src={values.paragraph_image instanceof File ? URL.createObjectURL(values.paragraph_image) : values.paragraph_image}
                            alt="paragraph_image"
                            onClick={() => {
                              inputRef.current.click();
                            }}
                          />
                        </UploadImage>
                        <br />
                        {(values.paragraph_image instanceof File || (values.paragraph_image && values.paragraph_image !== values.old_paragraph_image)) && (
                        <RemoveButtonWrapper>
                          <BootstrapButton
                            type="button"
                            onClick={() => {
                              setFieldValue('paragraph_image', null);
                            }}
                          >
                            <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
                          </BootstrapButton>
                        </RemoveButtonWrapper>
                        )}
                      </ImageWrapper>
                    ) : Number(id) && (values.paragraph_image || values.old_paragraph_image) ? (
                      <UploadImage>
                        <BootstrapImage
                          src={values.paragraph_image || values.old_paragraph_image}
                          alt="paragraph_image"
                          onClick={() => {
                            inputRef.current.click();
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.opacity = 0.8;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.opacity = 1;
                          }}
                        />
                      </UploadImage>
                    ) : (
                    <div>
                        <Button 
                      className="mb-0 w-auto"
                        type="button"
                        onClick={() => {
                          inputRef.current.click();
                        }}
                        variant={"primary"}
                      > 
                        Upload the image 
                      </Button>
                    </div>
                    )}
                  </FormGroup>
                  
                  <FormGroup
                    label="Enter a new paragraph *"
                    error={errors.paragraph && touched.paragraph ? errors.paragraph : ''}
                  >
                    <Form.Control
                      as="textarea"
                      rows={12}
                      {...getFieldProps('paragraph')}
                      isInvalid={errors.paragraph && touched.paragraph}
                      placeholder="Enter your paragraph here"
                    />
                  </FormGroup>
                  
                  <ButtonWrapper className="mt-3">
                    <Link to={`/${partnerId}/home/paragraph/view/${Number(id) ? id : 'create'}`}>
                      <Button variant="secondary" type="button">Go Back</Button>
                    </Link>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={saving || isSubmitting}
                    >
                      {saving || isSubmitting ? "Saving..." : "Save Changes"}
                    </Button>
                  </ButtonWrapper>
                </Form>
              );
            }}
          </Formik>
        </FormWrapper>
      }
    </>
  );
}

const FormWrapper = styled.div`
  max-width: 865px;
  margin: auto;
  padding: 10px;
`;

const Title = styled.h3`
  text-align: center;
  padding: 10px 0;
`;


const RemoveButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;  
  margin: 0 auto;
  position: absolute;
  top: -17px;
  right: -14px;
  @media (max-width:576px) {
    top: -9px;
    right: -17px;
  }
`;

const ButtonWrapper = styled.div`
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  @media (max-width: 890px) {
    gap: 2rem;
  }
  @media (max-width: 425px) {
    gap: 1rem;
  }
`;

const UploadImage = styled.div`
  max-width: 15vw;
  overflow: hidden;
  border-radius: 6px;
  @media (max-width: 576px) {
    max-width: 40vw;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  max-width: 15vw;
  @media (max-width:576px) {
    max-width: 40vw;
  }
`;

const BootstrapImage = styled('img')(({ theme }) => ({
  maxWidth: "15vw",
  cursor: "pointer",
  "@media (max-width: 576px)": {
    maxWidth: "40vw",
  },
}));

const BootstrapButton = styled('button')(({ theme }) => ({
  margin: '0 !important',
  backgroundColor: '#FFFFFF',
  padding: '5px 7px',
  border: '1px solid #dc3545',
  "&:hover": {
    background: '#fffeff',
    boxShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px'
  },
  '@media (max-width:576px)': {
    padding: '2px 4px',
  },
}));
